import {PayloadAction, createSlice} from '@reduxjs/toolkit'
import {ZadPurchase} from '../../../models/zad'
import {ZadApiError} from '../zad/errors'
import {
  cancelZadPurchase,
  createZadPurchase,
  executeZadPurchaseForceCancel,
  executeZadValidatedPurchaseCancel,
  fetchZadPurchaseById,
  fetchZadPurchasePickerProducts,
  fetchZadPurchasePickerSuppliers,
  fetchZadPurchases,
  importZadPurchase,
  updateZadPurchase,
  validateZadPurchase,
} from './service'
import {FetchZadPurchasesResponse, ZadPurchaseState} from './types'

const initialState: ZadPurchaseState = {
  purchases: [],
  total: 0,
  totalPages: 0,
  listLoading: false,
  listInitialized: false,
  actionLoading: false,
  selectedPurchase: null,
  pickerSuppliers: [],
  pickerProducts: [],
  pickerLoading: false,
  error: null,
  errorCode: null,
  errorStatus: null,
}

function applyRejected(state: ZadPurchaseState, action: PayloadAction<any>) {
  const payload = (action.payload || {}) as ZadApiError
  state.error = payload.error || 'Request failed'
  state.errorCode = payload.errorCode || null
  state.errorStatus = payload.status || null
}

function clearError(state: ZadPurchaseState) {
  state.error = null
  state.errorCode = null
  state.errorStatus = null
}

function upsertPurchase(state: ZadPurchaseState, purchase?: ZadPurchase | null) {
  if (!purchase?._id) return
  state.selectedPurchase = purchase
  const index = state.purchases.findIndex((item) => item._id === purchase._id)
  if (index >= 0) {
    state.purchases[index] = purchase
  }
}

const zadPurchaseSlice = createSlice({
  name: 'zadPurchases',
  initialState,
  reducers: {
    clearZadPurchaseError: (state: ZadPurchaseState) => {
      clearError(state)
    },
    clearSelectedZadPurchase: (state: ZadPurchaseState) => {
      state.selectedPurchase = null
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchZadPurchases.pending, (state: ZadPurchaseState) => {
      state.listLoading = true
      clearError(state)
    })
    builder.addCase(
      fetchZadPurchases.rejected,
      (state: ZadPurchaseState, action: PayloadAction<any>) => {
        applyRejected(state, action)
        state.listLoading = false
        state.listInitialized = true
      }
    )
    builder.addCase(
      fetchZadPurchases.fulfilled,
      (state: ZadPurchaseState, action: PayloadAction<FetchZadPurchasesResponse>) => {
        state.listLoading = false
        state.listInitialized = true
        clearError(state)
        state.purchases = action.payload.purchases
        state.total = action.payload.total
        state.totalPages = action.payload.totalPages
      }
    )

    builder.addCase(fetchZadPurchaseById.pending, (state: ZadPurchaseState) => {
      state.actionLoading = true
    })
    builder.addCase(
      fetchZadPurchaseById.rejected,
      (state: ZadPurchaseState, action: PayloadAction<any>) => {
        applyRejected(state, action)
        state.actionLoading = false
      }
    )
    builder.addCase(fetchZadPurchaseById.fulfilled, (state: ZadPurchaseState, action) => {
      state.actionLoading = false
      state.selectedPurchase = action.payload.purchase
    })

    builder.addCase(createZadPurchase.pending, (state: ZadPurchaseState) => {
      state.actionLoading = true
      clearError(state)
    })
    builder.addCase(
      createZadPurchase.rejected,
      (state: ZadPurchaseState, action: PayloadAction<any>) => {
        applyRejected(state, action)
        state.actionLoading = false
      }
    )
    builder.addCase(createZadPurchase.fulfilled, (state: ZadPurchaseState, action) => {
      state.actionLoading = false
      clearError(state)
      upsertPurchase(state, action.payload.purchase)
    })

    builder.addCase(importZadPurchase.pending, (state: ZadPurchaseState) => {
      state.actionLoading = true
      clearError(state)
    })
    builder.addCase(
      importZadPurchase.rejected,
      (state: ZadPurchaseState, action: PayloadAction<any>) => {
        applyRejected(state, action)
        state.actionLoading = false
      }
    )
    builder.addCase(importZadPurchase.fulfilled, (state: ZadPurchaseState, action) => {
      state.actionLoading = false
      clearError(state)
      upsertPurchase(state, action.payload.purchase)
    })

    builder.addCase(updateZadPurchase.pending, (state: ZadPurchaseState) => {
      state.actionLoading = true
      clearError(state)
    })
    builder.addCase(
      updateZadPurchase.rejected,
      (state: ZadPurchaseState, action: PayloadAction<any>) => {
        applyRejected(state, action)
        state.actionLoading = false
      }
    )
    builder.addCase(updateZadPurchase.fulfilled, (state: ZadPurchaseState, action) => {
      state.actionLoading = false
      clearError(state)
      upsertPurchase(state, action.payload.purchase)
    })

    builder.addCase(validateZadPurchase.pending, (state: ZadPurchaseState) => {
      state.actionLoading = true
      clearError(state)
    })
    builder.addCase(
      validateZadPurchase.rejected,
      (state: ZadPurchaseState, action: PayloadAction<any>) => {
        applyRejected(state, action)
        state.actionLoading = false
      }
    )
    builder.addCase(validateZadPurchase.fulfilled, (state: ZadPurchaseState, action) => {
      state.actionLoading = false
      clearError(state)
      upsertPurchase(state, action.payload.purchase)
    })

    builder.addCase(cancelZadPurchase.pending, (state: ZadPurchaseState) => {
      state.actionLoading = true
      clearError(state)
    })
    builder.addCase(
      cancelZadPurchase.rejected,
      (state: ZadPurchaseState, action: PayloadAction<any>) => {
        applyRejected(state, action)
        state.actionLoading = false
      }
    )
    builder.addCase(cancelZadPurchase.fulfilled, (state: ZadPurchaseState, action) => {
      state.actionLoading = false
      clearError(state)
      upsertPurchase(state, action.payload.purchase)
    })

    builder.addCase(executeZadValidatedPurchaseCancel.pending, (state: ZadPurchaseState) => {
      state.actionLoading = true
      clearError(state)
    })
    builder.addCase(
      executeZadValidatedPurchaseCancel.rejected,
      (state: ZadPurchaseState, action: PayloadAction<any>) => {
        applyRejected(state, action)
        state.actionLoading = false
      }
    )
    builder.addCase(executeZadValidatedPurchaseCancel.fulfilled, (state: ZadPurchaseState, action) => {
      state.actionLoading = false
      clearError(state)
      upsertPurchase(state, action.payload.purchase)
    })

    builder.addCase(executeZadPurchaseForceCancel.pending, (state: ZadPurchaseState) => {
      state.actionLoading = true
      clearError(state)
    })
    builder.addCase(
      executeZadPurchaseForceCancel.rejected,
      (state: ZadPurchaseState, action: PayloadAction<any>) => {
        applyRejected(state, action)
        state.actionLoading = false
      }
    )
    builder.addCase(executeZadPurchaseForceCancel.fulfilled, (state: ZadPurchaseState, action) => {
      state.actionLoading = false
      clearError(state)
      upsertPurchase(state, action.payload.purchase)
    })

    builder.addCase(fetchZadPurchasePickerSuppliers.pending, (state: ZadPurchaseState) => {
      state.pickerLoading = true
    })
    builder.addCase(fetchZadPurchasePickerSuppliers.rejected, (state: ZadPurchaseState) => {
      state.pickerLoading = false
    })
    builder.addCase(fetchZadPurchasePickerSuppliers.fulfilled, (state: ZadPurchaseState, action) => {
      state.pickerLoading = false
      state.pickerSuppliers = action.payload.suppliers
    })

    builder.addCase(fetchZadPurchasePickerProducts.pending, (state: ZadPurchaseState) => {
      state.pickerLoading = true
    })
    builder.addCase(fetchZadPurchasePickerProducts.rejected, (state: ZadPurchaseState) => {
      state.pickerLoading = false
    })
    builder.addCase(fetchZadPurchasePickerProducts.fulfilled, (state: ZadPurchaseState, action) => {
      state.pickerLoading = false
      state.pickerProducts = action.payload.products
    })
  },
})

export default zadPurchaseSlice.reducer
export const actions = zadPurchaseSlice.actions
export {initialState as zadPurchaseInitialState}
